import React, { useEffect, useMemo, useState } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import Alert from '@mui/material/Alert';
import MainModal from '../UI/Modal/MainModal';
import OvalSpinner from '../UI/Spinners/OvalSpinner';
import { loadResumeTemplateComponent } from '../../utils/resumeTemplateLoader';

const shellStyle = {
    width: '100%',
    height: '80vh',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    background: 'linear-gradient(145deg, rgba(18,41,47,0.95), rgba(10,27,33,0.88))',
    borderRadius: 24,
    padding: '24px clamp(12px, 2vw, 32px)',
    color: '#F8FFFF',
    position: 'relative',
    boxShadow: 'inset 0 0 45px rgba(6, 126, 143, 0.3)',
};

const neonHeaderStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    textAlign: 'center',
    color: '#ffffff',
    textShadow: '0 0 12px rgba(126, 240, 255, 0.45)',
};

const sublineStyle = {
    fontSize: '.85rem',
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    color: '#bff6ff',
    opacity: 0.9,
};

const viewerShellStyle = {
    width: '100%',
    flex: 1,
    minHeight: 0,
    borderRadius: 20,
    border: '1px solid rgba(126, 240, 255, 0.35)',
    boxShadow: '0 35px 80px rgba(3, 25, 34, 0.65), inset 0 0 35px rgba(62, 143, 147, 0.25)',
    overflow: 'hidden',
    background: 'linear-gradient(140deg, rgba(0,0,0,0.55), rgba(20,52,60,0.7))',
};

const loadingStateStyle = {
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const emptyStateStyle = {
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#98c7d0',
    fontSize: '.9rem',
    letterSpacing: '.03em',
};

const templateWidthHints = {
    Auckland: 780,
    BubbleFish: 860,
    EuroPass: 820,
    RadiantMoon: 900,
    Standard: 820,
    SwimmingElephant: 950,
    WaterTrain: 880,
};

const isBrowser = typeof window !== 'undefined';

const ResumePreviewModal = ({ open, resume, onClose, zIndex }) => {
    const [TemplateComponent, setTemplateComponent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [viewportWidth, setViewportWidth] = useState(isBrowser ? window.innerWidth : 1024);

    const resumeLabel = useMemo(() => resume?.storageDetails?.name || 'Selected Resume', [resume]);

    useEffect(() => {
        if (!isBrowser) return undefined;
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let active = true;

        const hydrateTemplate = async () => {
            if (!open || !resume) {
                setTemplateComponent(null);
                setErrorText('');
                setLoading(false);
                return;
            }

            setLoading(true);
            setErrorText('');
            setTemplateComponent(null);

            try {
                const LoadedTemplate = await loadResumeTemplateComponent(resume?.storageDetails?.template);
                if (active) {
                    setTemplateComponent(() => LoadedTemplate);
                }
            } catch (err) {
                if (active) {
                    setErrorText('Unable to render this resume preview. Try downloading instead.');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        hydrateTemplate();

        return () => {
            active = false;
        };
    }, [open, resume]);

    const modalWidth = useMemo(() => {
        if (!open) return '90vw';
        if (viewportWidth < 768) return '95vw';
        const templateKey = (resume?.storageDetails?.template || '').replace(/\s+/g, '');
        const baseWidth = templateWidthHints[templateKey] || 860;
        const padded = baseWidth + 160; // allow breathing room for padding + controls
        const clamped = Math.min(Math.max(padded, 720), 1180);
        const responsivePx = Math.min(clamped, viewportWidth - 120);
        return `${Math.max(responsivePx, 640)}px`;
    }, [open, viewportWidth, resume]);

    return (
        <MainModal isOpen={open} onClose={onClose} width={modalWidth} height="90vh" zIndex={zIndex}>
            <div style={shellStyle}>
                <div style={neonHeaderStyle}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 600 }}>{resumeLabel}</div>
                    <div style={sublineStyle}>Live Preview Mode</div>
                </div>

                {errorText && (
                    <Alert severity="error" sx={{ background: 'rgba(255,255,255,0.06)', color: '#ffd7d7', borderRadius: 2 }}>
                        {errorText}
                    </Alert>
                )}

                {loading && (
                    <div style={loadingStateStyle}>
                        <OvalSpinner size={56} color="#7ef0ff" />
                    </div>
                )}

                {!loading && TemplateComponent && (
                    <div style={viewerShellStyle}>
                        <PDFViewer style={{ width: '100%', height: '100%', border: 'none' }} showToolbar={false}>
                            <TemplateComponent resume={resume} imgUrl={resume?.storageDetails?.imgUrl} />
                        </PDFViewer>
                    </div>
                )}

                {!loading && !TemplateComponent && !errorText && (
                    <div style={emptyStateStyle}>Select a resume to preview.</div>
                )}
            </div>
        </MainModal>
    );
};

export default ResumePreviewModal;
